<svelte:window on:resize={resize} />

<Modal
    {id}
    wide
    bind:enabled
    titleText={i18n.addLearningObjectives}
    cancelText={i18n.cancel}
    confirmText={i18n.save}
    describedBy="{id}-picker-description"
    on:close={(e) => onClose(e.detail)}
>
    <div bind:this={loPicker} class="prom-lo-picker" slot="body" style={loPickerStyle}>
        <ul class="prom-lo-tree" role="tree" aria-labelledby="{id}-header">
            <LearningObjectiveTreeItem
                learningObjective={rootLearningObjective}
                {selectedLearningObjective}
                {learningObjectives}
                on:select={(e) => selectLO(e.detail)}
            />
        </ul>
        <section class="prom-lo-selected">
            {#if selectedLearningObjective}
                <h2>{selectedLearningObjective.title}</h2>
                {#if selectedDescription}
                    {#if renderLoDescriptionHtml}
                        <p id="{id}-picker-description">{@html selectedDescription}</p>
                    {:else}
                        <p id="{id}-picker-description">{selectedDescription}</p>
                    {/if}
                {/if}

                <div class="prom-lo-selected__list">
                    {#if selectedLearningObjective.childrenLoading}
                        <div class="prom-lo__loading">
                            <span class="screen-reader">{i18n.loading}</span>
                        </div>
                    {:else if !selectedLearningObjective.isFolder}
                        {i18n.noItems}
                    {:else if selectedChildren && selectedChildren.length}
                        <List>
                            <thead slot="header">
                                <ListElement header>
                                    <CheckboxCell
                                        bind:checked={headerChecked}
                                        text={i18n.selectAll}
                                        header
                                        on:change={(e) => headerCheckedChanged(e.detail)}
                                    />
                                    <LearningObjectiveCell text={i18n.learningObjectiveHeader} header />
                                </ListElement>
                            </thead>

                            <tbody slot="body">
                                {#each selectedChildren as lo (lo.id)}
                                    <ListElement bind:checked={lo.checked} checkOnClick={lo.selectable && !lo.disabled} on:click={() => itemClicked(lo)}>
                                        {#if lo.selectable}
                                            <CheckboxCell bind:checked={lo.checked} text={i18n.selected} on:change={checkedItemsChanged} disabled={lo.disabled}/>
                                        {:else}
                                            <Cell />
                                        {/if}
                                        <LearningObjectiveCell
                                            title={lo.title}
                                            {renderLoTitleHtml}
                                            description={lo.description}
                                            {renderLoDescriptionHtml}
                                            cssClass={lo.cssClass}
                                            selectable={lo.selectable}
                                            on:click={() => itemClicked(lo)}
                                        />
                                    </ListElement>
                                {/each}
                            </tbody>
                        </List>
                    {/if}
                </div>
            {/if}
        </section>
    </div>
</Modal>

<script>
    import Modal from '../../Modal/v2/Modal.svelte';
    import List from '../../../lists/List/v1/List.svelte';
    import ListElement from '../../../lists/ListElement/v1/ListElement.svelte';
    import CheckboxCell from '../../../lists/CheckboxCell/v1/CheckboxCell.svelte';
    import Cell from '../../../lists/Cell/v1/Cell.svelte';
    import LearningObjectiveCell from '../../../lists/LearningObjectiveCell/v1/LearningObjectiveCell.svelte';
    import LearningObjectiveTreeItem from './LearningObjectiveTreeItem.svelte';
    import { createEventDispatcher } from 'svelte';

    const dispatch = createEventDispatcher();

    /**
     * @typedef LearningObjective
     * @type {object}
     * @property {number} id
     * @property {number} parentId
     * @property {string} title
     * @property {string} description
     * @property {string} cssClass
     * @property {boolean} isFolder - If the lo contains other items within it
     * @property {boolean} [checked]
     * @property {boolean} [childrenLoading]
     * @property {boolean} [childrenLoaded]
     * @property {boolean} [disabled]
     */

    /** @type {string} Defaults to a random string */
    export let id = Math.random().toString(36).substr(2, 16);

    /** @type {boolean} */
    export let enabled = false;

    /** @type {boolean} */
    export let initialSelectionDisabled = false;

    /** @type {boolean} */
    export let renderLoTitleHtml = false;

    /** @type {boolean} */
    export let renderLoDescriptionHtml = false;

    /**
     * @callback getLearningObjectives
     * @param {number} nodeId - The parent id to return learning objectives for
     * @returns {Promise<LearningObjective[]>} Promise object resolving to an array of learning objectives
     */

    /** @type {getLearningObjectives} A method returning learning objectives for a parent */
    export let getLearningObjectives;
    /** @type { LearningObjective[] }*/
    export let initialLearningObjectives = [];

    /** @type {LearningObjective}*/
    export let rootLearningObjective = {
        id: 0,
        parentId: undefined,
        cssClass: '',
        title: '',
        description: '',
        isFolder: true,
    };

    /**
     * @typedef LearningObjectivePickerTerms
     * @type {object}
     * @property {string} [learningObjectivesConnectedToCourse]
     * @property {string} [save]
     * @property {string} [cancel]
     * @property {string} [addLearningObjectives]
     * @property {string} [selectAll]
     * @property {string} [learningObjectiveHeader]
     * @property {string} [selected]
     * @property {string} [noItems]
     * @property {string} [loading]
     */

    /** @type {LearningObjectivePickerTerms} i18n terms for the component */
    export let i18n = {};

    let loPicker;
    let reservedHeight = 150;
    let selectedChildren = [];
    let headerChecked = false;
    let learningObjectives = [];
    let selectedLearningObjective = null;

    $: if (enabled) {
        onEnable();
    }

    $: selectedDescription = selectedLearningObjective ? selectedLearningObjective.description : '';

    $: loPickerStyle = reservedHeight ? `height: calc(100vh - ${reservedHeight}px);` : '';

    function onEnable() {
        learningObjectives = [];
        selectedChildren = [];

        rootLearningObjective.checked = false;
        rootLearningObjective.childrenLoading = false;
        rootLearningObjective.childrenLoaded = false;
        rootLearningObjective.isFolder = true;

        selectLO(rootLearningObjective);
        setTimeout(resize, 0);
    }

    function selectLO(lo) {
        if (lo.id === 0 || !selectedLearningObjective || selectedLearningObjective.id !== lo.id) {
            selectedLearningObjective = lo;

            if (lo.isFolder && !lo.childrenLoading && !lo.childrenLoaded) {
                lo.childrenLoading = true;
                learningObjectives = [...learningObjectives];

                getLearningObjectives(lo.id || 0).then(newLOs => {
                    // Use the initialLearningObjectives array to pre-check LOs that are already selected
                    newLOs = newLOs.map(newLO => {
                        newLO.checked = initialLearningObjectives.some(initialLO => initialLO.id === newLO.id);

                        // If the initialSelectionDisabled setting is used then LO from the
                        // initialLearningObjectives are marked disabled and cannot be unchecked.
                        if (initialSelectionDisabled) {
                            newLO.disabled = newLO.checked;
                        }

                        return newLO;
                    });

                    lo.childrenLoading = false;
                    lo.childrenLoaded = true;
                    learningObjectives = [...learningObjectives, ...newLOs];

                    setSelectedChildren();
                });
            } else {
                setSelectedChildren();
            }
        }
    }

    function resize() {
        if (!enabled) {
            return;
        }

        // If the window width is smaller than the breakpoint do not set the height
        // instead the breakpoint will wrap the layout and the whole thing will scroll
        if (window.innerWidth < 640) {
            reservedHeight = null;

            return;
        }

        const modal = document.getElementById(id);
        const content = modal.querySelector('.prom-modal2__content');

        const scrollOverflow = content.scrollHeight - content.clientHeight;
        const totalModalHeight = modal.getBoundingClientRect().height;
        const loPickerHeight = loPicker.getBoundingClientRect().height;

        reservedHeight = totalModalHeight - (loPickerHeight - scrollOverflow);

        // If less than 250px space remain do not limit the height and let it overflow instead
        if (window.innerHeight - reservedHeight < 250) {
            reservedHeight = null;
        }
    }

    function setSelectedChildren() {
        selectedChildren = learningObjectives.filter(lo => lo.parentId === selectedLearningObjective.id);
        checkedItemsChanged();
    }

    function itemClicked(lo) {
        if (lo.selectable) {
            if (!lo.disabled) {
                checkedItemsChanged();
            }
        } else {
            selectLO(lo);
        }
    }

    function headerCheckedChanged({ checked }) {
        learningObjectives.filter(lo => lo.parentId === selectedLearningObjective.id).forEach((item) => {
            if (!item.disabled) {
                item.checked = checked && item.selectable;
            }
        });

        setSelectedChildren();
    }

    function checkedItemsChanged() {
        headerChecked = selectedChildren.every(i => i.checked || !i.selectable) && selectedChildren.some(i => i.checked);
    }

    function onClose({ confirmed }) {
        if (confirmed) {
            const checkedLO = learningObjectives.filter(lo => lo.checked);

            // If any of the initial LO were never loaded, include them in the return since they had
            // to chance to be unchecked but were never loaded into the learningObjectives array
            initialLearningObjectives.forEach(initialLO => {
                if (!learningObjectives.find(lo => initialLO.id === lo.id)) {
                    checkedLO.push(initialLO);
                }
            });

            dispatch('apply', checkedLO);
        }
    }
</script>
